
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UseListingProps {
  listingId: string;
}

// Helper function to validate UUID format
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const useListing = ({ listingId }: UseListingProps) => {
  return useQuery({
    queryKey: ['listing', listingId],
    queryFn: async () => {
      // Check if listingId is a valid UUID
      if (!listingId || !isValidUUID(listingId)) {
        console.log('Invalid listing ID provided:', listingId);
        throw new Error('Invalid listing ID');
      }

      const { data, error } = await supabase
        .from('listings')
        .select(`
          id,
          title,
          description,
          price,
          price_currency,
          breed,
          type,
          age,
          weight,
          weight_unit,
          location,
          location_coordinates,
          images,
          evaluation_type,
          calving_ease,
          birth_weight,
          weaning_weight,
          yearling_weight,
          milk,
          total_maternal,
          carcass_weight,
          marbling,
          intramuscular_fat,
          fat_thickness,
          maternal_weaning_weight,
          ribeye_area,
          gestation_length,
          scrotal_size,
          day_200_weight,
          day_400_weight,
          day_600_weight,
          eye_muscle_area,
          rump_fat,
          retail_beef_yield,
          registration_number,
          youtube_video_url,
          calving_ease_acc, calving_ease_perc_rank,
          birth_weight_acc, birth_weight_perc_rank,
          weaning_weight_acc, weaning_weight_perc_rank,
          yearling_weight_acc, yearling_weight_perc_rank,
          milk_acc, milk_perc_rank,
          total_maternal_acc, total_maternal_perc_rank,
          carcass_weight_acc, carcass_weight_perc_rank,
          marbling_acc, marbling_perc_rank,
          intramuscular_fat_acc, intramuscular_fat_perc_rank,
          fat_thickness_acc, fat_thickness_perc_rank,
          maternal_weaning_weight_acc, maternal_weaning_weight_perc_rank,
          ribeye_area_acc, ribeye_area_perc_rank,
          gestation_length_acc, gestation_length_perc_rank,
          scrotal_size_acc, scrotal_size_perc_rank,
          day_200_weight_acc, day_200_weight_perc_rank,
          day_400_weight_acc, day_400_weight_perc_rank,
          day_600_weight_acc, day_600_weight_perc_rank,
          eye_muscle_area_acc, eye_muscle_area_perc_rank,
          rump_fat_acc, rump_fat_perc_rank,
          retail_beef_yield_acc, retail_beef_yield_perc_rank,
          breeder_id,
          status,
          created_at
        `)
        .eq('id', listingId)
        .in('status', ['active', 'upcoming'])
        .single();

      if (error) {
        console.error('Error fetching listing:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Listing not found');
      }

      // Get the breeder profile - use maybeSingle() to handle cases where profile doesn't exist
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, business_name, first_name, last_name, profile_image_url, location, location_coordinates')
        .eq('id', data.breeder_id)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching breeder profile:', profileError);
        throw profileError;
      }

      // Handle case where no profile exists
      const breederName = profile?.business_name || 
                         (profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '') || 
                         'Unknown Breeder';

      // Handle images - convert from Json to string
      let imageUrl = "https://images.unsplash.com/photo-1493962853295-0fd70327578a?w=800&h=600&fit=crop";
      if (data.images && Array.isArray(data.images) && data.images.length > 0) {
        imageUrl = String(data.images[0]);
      }

      // Parse location coordinates from listing for the map
      let coordinates = { lat: 31.9686, lng: -99.9018 }; // Default fallback coordinates (center of USA)
      
      if (data.location_coordinates) {
        try {
          let parsedCoordinates;
          if (typeof data.location_coordinates === 'string') {
            parsedCoordinates = JSON.parse(data.location_coordinates);
          } else {
            parsedCoordinates = data.location_coordinates;
          }
          
          if (parsedCoordinates && typeof parsedCoordinates === 'object' && 
              'longitude' in parsedCoordinates && 'latitude' in parsedCoordinates &&
              typeof parsedCoordinates.longitude === 'number' && 
              typeof parsedCoordinates.latitude === 'number') {
            coordinates = {
              lat: parsedCoordinates.latitude,
              lng: parsedCoordinates.longitude
            };
          }
        } catch (error) {
          console.log('Error parsing location coordinates:', error);
        }
      }

      return {
        ...data,
        image: imageUrl,
        breeder: {
          id: data.breeder_id,
          name: breederName,
          image: profile?.profile_image_url || null,
          location: profile?.location || "Location not specified",
          lat: coordinates.lat,
          lng: coordinates.lng
        }
      };
    },
  });
};
